import { Box, Button, Card, CloseButton, Flex, Heading, Spacer } from "@chakra-ui/react";
import { data } from "jquery";
import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from "react";

export function PopupButton(props) {
  return (
    <Button onClick={props.onClick} isDisabled={props.disabled} colorScheme='blue' size="sm" ml={2}>
      {props.children}
    </Button>
  );
}

const BLOCK_DURATION = 1300;
const DEFAULT_MIN_WIDTH = 'auto';

const PopupLayout = forwardRef(({
  id,
  maxHeight,
  pageX,
  pageY,
  title,
  closeFunction,
  minWidth = DEFAULT_MIN_WIDTH,
  minHeight,
  overflow,
  layoutRef,
  noSaveSize = false, 
  ...props
}, ref) => {
  const storageKey = `popup-position-${id || title}`.toLowerCase().replace(/\s+/g, '-');
  
  const getInitialPosition = useCallback(() => {
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      const { x, y } = JSON.parse(saved);
      return { x, y };
    }
    return { x: pageX, y: pageY };
  }, [storageKey]);

  

  const getInitialSize = useCallback(() => {

    if (noSaveSize) {
      return { width: minWidth, height: minHeight || 'auto' };
    }
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      const { width, height } = JSON.parse(saved);
      return { width, height };
    }
    return { width: minWidth, height: minHeight || 'auto' };
  }, [storageKey, minWidth, minHeight, noSaveSize]);

  const [position, setPosition] = useState(() => getInitialPosition());
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState(getInitialSize);
  const [zIndex, setZIndex] = useState(1000);
  const [isClosing, setIsClosing] = useState(false);
  
  const componentRef = useRef(null);
  const contentRef = useRef(null);
  const closeButtonRef = useRef(null);
  const dragStartRef = useRef(null);
  const resizeStartRef = useRef(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const cleanupTimeoutRef = useRef(null);
  const lastMousePosition = useRef(null);

  // Cleanup function for all timeouts and refs
  const cleanup = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
    document.body.style.overflow = 'auto';
    isScrollingRef.current = false;
    dragStartRef.current = null;
    lastMousePosition.current = null;
    resizeStartRef.current = null;
  }, []);

  const saveCurrentPosition = useCallback(() => {
    const dataToSave = { x: position.x, y: position.y };
    // NEW: Only add size if noSaveSize is false
    if (!noSaveSize) {
      dataToSave.width = size.width;
      dataToSave.height = size.height;
    }
    sessionStorage.setItem(storageKey, JSON.stringify(
      dataToSave));
  }, [position.x, position.y, size, storageKey, noSaveSize]);

  useImperativeHandle(layoutRef, () => ({
    saveCurrentPosition
  }), [saveCurrentPosition]);

  useEffect(() => {
    const saved = sessionStorage.getItem(storageKey);
    if (!saved) {
      setPosition({ x: pageX, y: pageY });
    }
  }, [storageKey]);

  // Component unmount cleanup
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Mouse position tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      lastMousePosition.current = { x: e.clientX, y: e.clientY };
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll handling
  useEffect(() => {
    const handleWheel = (e) => {
      if (isClosing) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      
      const isInsidePopup = contentRef.current?.contains(e.target);

      if (isInsidePopup) {
        isScrollingRef.current = true;
        
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        
        scrollTimeoutRef.current = setTimeout(() => {
          isScrollingRef.current = false;
          scrollTimeoutRef.current = null;
        }, 150);
        
        return;
      }

      if (isScrollingRef.current) {
        e.preventDefault();
        e.stopPropagation();

        if (contentRef.current) {
          contentRef.current.scrollTop += e.deltaY;
        }

        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        
        scrollTimeoutRef.current = setTimeout(() => {
          isScrollingRef.current = false;
          scrollTimeoutRef.current = null;
        }, 150);
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isClosing]);

  // Closing animation handling
  useEffect(() => {
    if (!isClosing) return;

    const handleRemainingWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleMouseMove = (e) => {
      e.preventDefault();
      lastMousePosition.current = { x: e.clientX, y: e.clientY };
    };

    document.addEventListener('wheel', handleRemainingWheel, { passive: false, capture: true });
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    
    const timeoutId = setTimeout(() => {
      document.removeEventListener('wheel', handleRemainingWheel, { capture: true });
      document.removeEventListener('mousemove', handleMouseMove);
      closeFunction();
    }, 150);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('wheel', handleRemainingWheel, { capture: true });
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isClosing, closeFunction]);

  // Dragging functionality
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      if (!dragStartRef.current) return;

      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      const newX = Math.max(0, Math.min(
        dragStartRef.current.startX + deltaX,
        window.innerWidth - (componentRef.current?.offsetWidth || 0)
      ));

      const newY = Math.max(0, Math.min(
        dragStartRef.current.startY + deltaY,
        window.innerHeight - 50  // Approximate header height
      ));

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      dragStartRef.current = null;
    };
  }, [isDragging]);

  // Resizing functionality
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      if (!resizeStartRef.current) return;

      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;

      const newWidth = `${Math.max(parseInt(minWidth, 10), resizeStartRef.current.startWidth + deltaX)}px`;
      const newHeight = `${Math.max(parseInt(minHeight || 0, 10), resizeStartRef.current.startHeight + deltaY)}px`;

      setSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizeStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      resizeStartRef.current = null;
    };
  }, [isResizing, minWidth, minHeight]);

  const blockAllScrolling = useCallback(() => {
    const blocker = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('wheel', blocker, { passive: false, capture: true });
    document.addEventListener('scroll', blocker, { passive: false, capture: true });
    document.body.style.overflow = 'hidden';
    
    const timeoutId = setTimeout(() => {
      document.removeEventListener('wheel', blocker, { capture: true });
      document.removeEventListener('scroll', blocker, { capture: true });
      document.body.style.overflow = 'auto';
    }, BLOCK_DURATION);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('wheel', blocker, { capture: true });
      document.removeEventListener('scroll', blocker, { capture: true });
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('button, input, select, textarea, a')) return;
    
    e.preventDefault();
    setIsDragging(true);
    setZIndex(z => z + 1);

    const rect = componentRef.current.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startX: rect.left,
      startY: rect.top
    };
  }, []);

  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    setZIndex(z => z + 1);

    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startWidth: parseInt(size.width, 10),
      startHeight: parseInt(size.height, 10)
    };
  }, [size.width, size.height]);

  const handleClose = useCallback(() => {

    if (ref?.current?.canClose && !ref.current.canClose()) {
      return;
    }

    // NEW: Create an object with at least position data
    const dataToSave = { x: position.x, y: position.y };
    
    // NEW: Only add size if noSaveSize is false
    if (!noSaveSize) {
      dataToSave.width = size.width;
      dataToSave.height = size.height;
    }
    
    sessionStorage.setItem(storageKey, JSON.stringify(dataToSave));
    
    const cleanupBlockScroll = blockAllScrolling();
    
    cleanup();
    
    const closeTimeoutId = setTimeout(() => {
      setIsClosing(true);
      closeFunction();
    }, 50);

    cleanupTimeoutRef.current = setTimeout(() => {
      cleanupBlockScroll();
    }, BLOCK_DURATION);

    return () => {
      clearTimeout(closeTimeoutId);
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
      cleanupBlockScroll();
    };
  }, [blockAllScrolling, closeFunction, position.x, position.y, size, storageKey, cleanup]);

  

  return (
    <Card 
      ref={componentRef}
      position="fixed"
      zIndex={zIndex}
      width={size.width}
      height={size.height}
      top={`${position.y}px`}
      left={`${position.x}px`}
      borderRadius="10px"
      boxShadow="lg"
      bgColor="white"
      overflow="hidden"
      style={{ 
        height: size.height === 'auto' ? 'auto' : `calc(${size.height} + 1px)`
      }}
    >
      <Flex 
        onMouseDown={handleMouseDown}
        cursor={isDragging ? 'grabbing' : 'grab'}
        background="white"
        width="100%"
        zIndex={1}
        pt={4}
        pb={3}
        pl={4}
        pr={2}
        borderBottom="1px solid #eee"
        justifyContent="space-between"
        alignItems="center"
      >
        <Heading size="sm">{title}</Heading>
        <Spacer />
        <Box ref={closeButtonRef}>
          <CloseButton onClick={handleClose} size="sm" />
        </Box>
      </Flex>

      <Box
        ref={contentRef}
        overflow="auto"
        maxHeight={maxHeight}
        css={{
        // Only add Chrome-specific containment property
        '@media screen and (-webkit-min-device-pixel-ratio:0)': {
          contain: 'paint'
        },
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          width: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '24px',
        },
      }}
      >
        <Box p={2}>
          {props.children}
        </Box>
        
        {props.buttons && (
          <Flex 
            mt={4}
            pt={4}
            pb={4}
            pl={4}
            pr={2}
            borderTop="1px solid #eee"
            alignItems="center"
            justifyContent="flex-end"
          >
            {props.buttons}
          </Flex>
        )}
      </Box>

      <Box
        position="absolute"
        bottom={0}
        right={0}
        width="10px"
        height="10px"
        bgColor="transparent"
        cursor="se-resize"
        onMouseDown={handleResizeMouseDown}
      />
    </Card>
  )});


export { PopupLayout };